'use client';
import { useSyncExternalStore } from 'react';

/**
 * '다음 강의' 안내를 쓸지 — **이 브라우저에 남는 값**이다(설정 화면의 토글, 2026-09-16 사용자 지시).
 * 화면 모드(테마)와 같은 결이라 계정이 아니라 `localStorage` 에 둔다. 재생 창은 같은 출처의 새 창이라 이 값을 그대로 읽는다.
 *
 * **이 파일은 클라이언트 전용이다** — 훅(`useSyncExternalStore`)을 쓰므로, 서버 컴포넌트가 읽는 `player-shared.ts` 에 두면
 * Next 가 통째로 막는다(2026-09-16 실측: dev 전 페이지 500). 주소 조립 같은 공용 값은 그쪽, 브라우저에 남는 값은 여기다.
 * **PC 재생 창에만 해당한다** — 모바일은 앱이 틀어서 우리 화면이 아니다.
 */
export const NEXT_UP_KEY = 'sp_next_up';

/** 기본은 켬 — 값이 없거나 저장을 못 읽는 브라우저도 켠 것으로 본다 */
export function nextUpEnabled(): boolean {
  try { return localStorage.getItem(NEXT_UP_KEY) !== 'off'; } catch { return true; }
}

const listeners = new Set<() => void>();

export function setNextUpEnabled(on: boolean): void {
  try { localStorage.setItem(NEXT_UP_KEY, on ? 'on' : 'off'); } catch { /* 저장 못 해도 이 창에서는 바뀐다 */ }
  listeners.forEach((fn) => fn());
}

/**
 * 저장된 값을 읽는 훅 — **브라우저 저장소는 React 바깥의 값**이라 `useSyncExternalStore` 로 읽는다.
 * 효과 안에서 `setState` 로 읽으면 렌더가 한 번 더 돌고(그 규칙을 린트가 막는다) 서버 렌더와도 어긋난다.
 * 서버에서는 기본(켬)으로 그린다 — 저장소가 없는 자리다.
 */
export function useNextUpEnabled(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      window.addEventListener('storage', onChange); // 다른 창(설정 화면)에서 바꾼 것도 받는다
      return () => { listeners.delete(onChange); window.removeEventListener('storage', onChange); };
    },
    nextUpEnabled,
    () => true,
  );
}

/**
 * 재생 진단 화면을 쓸지 — **개발·점검용**(2026-09-17 사용자 지시 "새 창 띄워서 agent 정보와 어떤 액션을 할지 찍어 달라").
 * 켜면 [스트리밍] 이 재생 창을 `?debug=1` 로 열고, 재생 전에 기기·판정·다음 동작을 보여 준 뒤 [확인] 을 눌러야 진행한다.
 * 화면 모드처럼 **이 브라우저에만** 남는다 — 다른 회원에게 영향이 없다.
 */
export const PLAYER_DEBUG_KEY = 'sp_player_debug';

export function debugEnabled(): boolean {
  try { return localStorage.getItem(PLAYER_DEBUG_KEY) === 'on'; } catch { return false; }
}

export function setDebugEnabled(on: boolean): void {
  try { localStorage.setItem(PLAYER_DEBUG_KEY, on ? 'on' : 'off'); } catch { /* 저장 못 해도 이 창에서는 바뀐다 */ }
  listeners.forEach((fn) => fn());
}

/** 저장된 값을 읽는 훅 — `useNextUpEnabled` 와 같은 방식(외부 저장소) */
export function useDebugEnabled(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      window.addEventListener('storage', onChange);
      return () => { listeners.delete(onChange); window.removeEventListener('storage', onChange); };
    },
    debugEnabled,
    () => false,
  );
}

